import { useState, useEffect, useMemo } from "react";
import { useLanguage } from "../../context/LanguageContext";

/**
 * Extract tax domain, mentioned regulations, and dispute keywords from user query.
 */
function extractQueryContext(question = "") {
  const q = (question || "").toLowerCase();

  // 1. Detect regulation numbers (PMK, UU, PER, PP, SE)
  const regPatterns = [
    /\b(pmk\s*(?:no\.?|nomor)?\s*\d+(?:\s*(?:tahun|thn|\/)?\s*\d{4})?)/gi,
    /\b(per\s*-\s*\d+(?:\/[a-z0-9.]+\/\d{4})?)/gi,
    /\b(uu\s*(?:kup|pph|ppn|hpp)?(?:\s*no\.?\s*\d+)?(?:\s*(?:tahun|thn|\/)?\s*\d{4})?)/gi,
    /\b(pp\s*(?:no\.?|nomor)?\s*\d+(?:\s*(?:tahun|thn|\/)?\s*\d{4})?)/gi,
    /\b(se\s*-\s*\d+\/[a-z0-9.]+\/\d{4})/gi,
  ];
  const detectedRegs = [];
  regPatterns.forEach((regex) => {
    const matches = question.match(regex);
    if (matches) {
      matches.forEach((m) => {
        const clean = m.trim().toUpperCase();
        if (!detectedRegs.includes(clean)) detectedRegs.push(clean);
      });
    }
  });

  // 2. Classify dispute / procedural domain
  let domain = "general";
  if (
    q.includes("sp2dk") ||
    q.includes("stp") ||
    q.includes("sanksi") ||
    q.includes("penghapusan sanksi") ||
    q.includes("pengurangan sanksi")
  ) {
    domain = "sp2dk_stp";
  } else if (
    q.includes("sphp") ||
    q.includes("pemeriksaan") ||
    q.includes("closing conference") ||
    q.includes("jamuan") ||
    q.includes("nominatif")
  ) {
    domain = "sphp";
  } else if (
    q.includes("keberatan") ||
    q.includes("skpkb") ||
    q.includes("pasal 25")
  ) {
    domain = "keberatan";
  } else if (
    q.includes("banding") ||
    q.includes("pengadilan pajak") ||
    q.includes("pasal 27")
  ) {
    domain = "banding";
  } else if (
    q.includes("pph 21") ||
    q.includes("gaji") ||
    q.includes("natura") ||
    q.includes("ter") ||
    q.includes("ptkp")
  ) {
    domain = "pph21";
  } else if (
    q.includes("ppn") ||
    q.includes("faktur") ||
    q.includes("ekualisasi")
  ) {
    domain = "ppn";
  }

  // 3. Extract tax years
  const yearMatches = question.match(/\b(20[12]\d)\b/g);
  const taxYears = yearMatches ? [...new Set(yearMatches)] : [];

  return { detectedRegs, domain, taxYears };
}

/**
 * Generate contextual cognitive stages and live thoughts based on query context and seconds.
 */
function buildThinkingPlan(context, seconds, isEnglish) {
  const { detectedRegs, domain, taxYears } = context;
  const regListStr =
    detectedRegs.length > 0
      ? detectedRegs.slice(0, 3).join(", ")
      : "PMK 118/2024 jo. PMK 71/2026";
  const yearsStr =
    taxYears.length > 0 ? `Tahun Pajak ${taxYears.join(" & ")}` : "Tahun Pajak terkait";

  let steps = [];
  let liveThought = "";

  if (domain === "sp2dk_stp") {
    steps = [
      {
        after: 0,
        title: isEnglish
          ? "Deconstructing SP2DK, Amended Return, & STP Penalty Dispute"
          : "Membedah Kasus SP2DK, Pembetulan SPT, & Penerbitan STP",
        desc: isEnglish
          ? `Analyzing tax audit timeline: follow-up on 2 SP2DKs (${yearsStr}), tax return amendment, and issuance of administrative penalty assessment (STP).`
          : `Mengurai kronologi sengketa: tindak lanjut 2 SP2DK (${yearsStr}), pembetulan SPT Tahunan, dan penerbitan STP sanksi bunga/denda.`,
      },
      {
        after: 5,
        title: isEnglish
          ? "Statutory Knowledge Retrieval & Regulation Cross-Referencing"
          : "Penelusuran Regulasi & Klausul Penghapusan Sanksi",
        desc: isEnglish
          ? `Retrieving statutory provisions for ${regListStr} and Article 36 paragraph (1) letter a of the General Tax Provisions Law (UU KUP).`
          : `Mengambil ketentuan ${regListStr} jo. Pasal 36 ayat (1) huruf a UU KUP pada knowledge base perpajakan.`,
      },
      {
        after: 16,
        title: isEnglish
          ? "Comparative Legal Analysis & DGT Discretionary Power"
          : "Analisis Komparasi Kebijakan & Diskresi Dirjen Pajak",
        desc: isEnglish
          ? "Examining the evolution of DGT discretion for penalty waiver/reduction due to taxpayer oversight (kekhilafan) vs. no-fault circumstances."
          : "Menelaah perbedaan kriteria kekhilafan Wajib Pajak vs bukan kesalahannya, serta ruang diskresi Direktur Jenderal Pajak antar-regulasi.",
      },
      {
        after: 32,
        title: isEnglish
          ? "Legal Citation Verification (Chain-of-Verification / CoVe)"
          : "Verifikasi Sitasi Hukum (Chain-of-Verification / CoVe)",
        desc: isEnglish
          ? "Verifying statutory validity, revoking status of PMK 8/2013 by PMK 118/2024, and transitional provisions under PMK 71/2026."
          : "Memeriksa status pencabutan PMK 8/2013 oleh PMK 118/2024, ketentuan peralihan PMK 71/2026, dan hierarki hukum UU KUP.",
      },
      {
        after: 52,
        title: isEnglish
          ? "Substantive Review of Formal Requirements & Procedural Rights"
          : "Review Substantif Prosedur & Syarat Permohonan",
        desc: isEnglish
          ? "Verifying principal payment prerequisites, 1-letter-1-STP rules, filing deadlines, and mitigating risks of formal rejection."
          : "Memverifikasi syarat pelunasan pokok pajak, ketentuan 1 surat per STP, batas waktu pengajuan, dan mitigasi risiko penolakan formal.",
      },
      {
        after: 75,
        title: isEnglish
          ? "Final Synthesis & Comprehensive Legal Opinion Formulation"
          : "Sintesis Akhir & Perumusan Opini Yuridis Komparatif",
        desc: isEnglish
          ? "Finalizing detailed structured legal advisory with comparative matrices, statutory basis, and tactical recommendations."
          : "Memfinalisasi telaah yuridis komparatif yang terstruktur, lengkap dengan dasar hukum operasional dan rekomendasi taktis Wajib Pajak.",
      },
    ];

    if (seconds < 8) {
      liveThought = `Mengidentifikasi pokok sengketa: 2 SP2DK (${yearsStr}), kepatuhan pembetulan SPT, dan permohonan penghapusan sanksi STP...`;
    } else if (seconds < 20) {
      liveThought = `Pencarian database regulasi: mengekstrak klausul Pasal 36 UU KUP, PMK 118/2024, dan PMK 71/2026...`;
    } else if (seconds < 36) {
      liveThought = `Membandingkan ruang diskresi Direktur Jenderal Pajak: PMK 8/2013 vs PMK 118/2024 mengenai kriteria kekhilafan...`;
    } else if (seconds < 55) {
      liveThought = `Menjalankan Chain-of-Verification (CoVe): memastikan pasal yang dirujuk masih berlaku dan tidak terpotong...`;
    } else if (seconds < 75) {
      liveThought = `Menelaah aspek prosedural: apakah pembayaran pokok pajak mendahului permohonan menggugurkan hak permohonan sanksi...`;
    } else if (seconds < 95) {
      liveThought = `Mengevaluasi ketentuan peralihan PMK 71/2026 terhadap sanksi STP yang diterbitkan atas pembetulan sukarela...`;
    } else if (seconds < 115) {
      liveThought = `Melakukan review kritis terhadap kelengkapan dalil hukum agar jawaban komprehensif dan bebas dari kontradiksi...`;
    } else {
      liveThought = `Menuntaskan format perumusan jawaban komparatif dan langkah teknis permohonan penghapusan sanksi ke KPP...`;
    }
  } else if (domain === "sphp") {
    steps = [
      {
        after: 0,
        title: isEnglish ? "Audit Findings & SPHP Analysis" : "Membedah Temuan Pemeriksa & Pokok SPHP",
        desc: isEnglish
          ? "Deconstructing auditor correction findings, disputed accounts, and formal closing conference schedule."
          : "Menganalisis pos koreksi pemeriksa, pembuktian biaya 3M, dan jadwal Pembahasan Akhir (Closing Conference).",
      },
      {
        after: 6,
        title: isEnglish ? "Retrieving Statutory Provisions & Evidentiary Rules" : "Penelusuran Regulasi Biaya 3M & Nominatif",
        desc: isEnglish
          ? "Cross-referencing Article 6 paragraph (1) letter a Income Tax Law (UU PPh) and PMK 02/2010 on nominative lists."
          : "Mengambil ketentuan Pasal 6 ayat (1) huruf a UU PPh dan PMK 02/PMK.03/2010 mengenai daftar nominatif biaya.",
      },
      {
        after: 18,
        title: isEnglish ? "Constructing FRAC Substantive Arguments" : "Perumusan Bantahan Yuridis Berbasis FRAC",
        desc: isEnglish
          ? "Formulating Fact-Rule-Analysis-Conclusion arguments to rebut auditor assertions with valid documentary evidence."
          : "Menyusun bantahan yuridis per pos koreksi berdasarkan bukti invoice asli, transfer bank, dan daftar nominatif.",
      },
      {
        after: 35,
        title: isEnglish ? "Legal Citation Verification & Circular Cross-Check" : "Verifikasi Sitasi Hukum & Surat Edaran DJP",
        desc: isEnglish
          ? "Verifying statutory hierarchy and ensuring arguments align with Tax Court jurisprudence on deductible expenses."
          : "Memvalidasi hierarki peraturan, status keberlakuan PMK, dan preseden Putusan Pengadilan Pajak terkait.",
      },
      {
        after: 55,
        title: isEnglish ? "Reviewing Closing Conference Strategy & Rights" : "Review Substantif Strategi Closing Conference",
        desc: isEnglish
          ? "Formulating formal requests for closing conference, rights reservation, and quality assurance team review."
          : "Memastikan hak formal Wajib Pajak terlindungi dalam Risalah Pembahasan Akhir Hasil Pemeriksaan (PAHP).",
      },
      {
        after: 75,
        title: isEnglish ? "Synthesizing Written Response Draft" : "Sintesis Akhir Draf Surat Tanggapan SPHP",
        desc: isEnglish
          ? "Finalizing comprehensive written response with correction comparison tables and petitum."
          : "Menuntaskan format tanggapan formal lengkap dengan matriks koreksi komparatif dan permohonan pembatalan.",
      },
    ];

    if (seconds < 10) {
      liveThought = "Membedah pokok temuan pemeriksa dan mengidentifikasi pos koreksi yang disengketakan...";
    } else if (seconds < 25) {
      liveThought = "Menelaah kepatuhan syarat formal daftar nominatif PMK 02/2010 dan prinsip 3M Pasal 6 UU PPh...";
    } else if (seconds < 50) {
      liveThought = "Menyusun sanggahan teknis yuridis dan menguji kesesuaian bukti dokumen pendukung...";
    } else if (seconds < 80) {
      liveThought = "Menjalankan verifikasi sitasi (CoVe) dan memastikan ketepatan rujukan regulasi pemeriksaan...";
    } else {
      liveThought = "Menuntaskan susunan draf tanggapan resmi dan strategi pembahasan akhir (closing conference)...";
    }
  } else if (domain === "keberatan") {
    steps = [
      {
        after: 0,
        title: isEnglish ? "Deconstructing Assessment (SKPKB) & Formal Grounds" : "Membedah SKPKB & Syarat Formal Pasal 25 UU KUP",
        desc: isEnglish
          ? "Verifying 3-month statutory deadline from receipt date, 1-SKP-per-letter rule, and principal payment requirements."
          : "Memeriksa tenggat waktu 3 bulan sejak tanggal terima SKPKB, syarat 1 surat 1 SKP, dan bukti pelunasan pokok pajak.",
      },
      {
        after: 6,
        title: isEnglish ? "Retrieving Statutory Bases & Precedents" : "Penelusuran Dasar Hukum & Yurisprudensi",
        desc: isEnglish
          ? "Retrieving applicable tax laws, ministerial decrees, and relevant Tax Court decisions."
          : "Mengambil ketentuan PMK 118/2024, UU PPh/PPN, dan yurisprudensi sengketa terkait pada basis data perpajakan.",
      },
      {
        after: 20,
        title: isEnglish ? "Constructing Dispute Matrix & Counter-Arguments" : "Penyusunan Matriks Sengketa & Bantahan FRAC",
        desc: isEnglish
          ? "Building dispute breakdown: auditor positions vs taxpayer positions with statutory counter-arguments."
          : "Menyusun matriks komparasi perhitungan pajak menurut SKPKB vs menurut Wajib Pajak secara detail.",
      },
      {
        after: 38,
        title: isEnglish ? "Citation Verification & Risk Evaluation" : "Verifikasi Sitasi Hukum & Evaluasi Sanksi 30%",
        desc: isEnglish
          ? "Verifying regulation validity and evaluating statutory 30% penalty risk under Article 25 paragraph (9) UU KUP."
          : "Memvalidasi sitasi pasal dan menghitung mitigasi risiko sanksi denda 30% jika keberatan ditolak.",
      },
      {
        after: 60,
        title: isEnglish ? "Substantive Review & Petitum Formulation" : "Review Substantif & Perumusan Petitum",
        desc: isEnglish
          ? "Ensuring all dispute items are backed by verified evidence and formulating legal prayers (petitum)."
          : "Memastikan seluruh pos koreksi didukung alat bukti sah dan merumuskan petitum pembatalan koreksi.",
      },
      {
        after: 80,
        title: isEnglish ? "Finalizing Formal Objection Letter" : "Finalisasi Draf Surat Keberatan Resmi",
        desc: isEnglish
          ? "Finalizing complete legal brief ready for submission to Regional Tax Office (Kanwil DJP)."
          : "Menyelesaikan penyusunan naskah formal Surat Keberatan lengkap dengan daftar lampiran pembuktian.",
      },
    ];

    if (seconds < 10) {
      liveThought = "Memeriksa syarat formal Pasal 25 UU KUP: jangka waktu 3 bulan dan pelunasan jumlah yang disetujui...";
    } else if (seconds < 30) {
      liveThought = "Menyusun matriks pos sengketa: memisahkan pos yang disetujui dan pos yang diajukan keberatan...";
    } else if (seconds < 60) {
      liveThought = "Menjalankan Chain-of-Verification (CoVe) terhadap pasal koreksi penyusutan / biaya usaha...";
    } else if (seconds < 90) {
      liveThought = "Melakukan review substantif: menguji kelengkapan bukti pembukuan dan memitigasi sanksi 30%...";
    } else {
      liveThought = "Menuntaskan petitum permohonan dan struktur naskah legal brief Surat Keberatan...";
    }
  } else {
    // General tax inquiry
    steps = [
      {
        after: 0,
        title: isEnglish ? "Deconstructing Query & Tax Context" : "Membedah Pertanyaan & Konteks Perpajakan",
        desc: isEnglish
          ? "Identifying core legal issues, taxpayer profile, and relevant tax categories."
          : "Mengidentifikasi pokok permasalahan hukum, subjek pajak, dan kategori jenis pajak terkait.",
      },
      {
        after: 5,
        title: isEnglish ? "Knowledge Retrieval in Tax Law Database" : "Penelusuran Regulasi & Database Hukum Pajak",
        desc: isEnglish
          ? `Searching dual-vector database (Pinecone) for relevant regulations (${regListStr}).`
          : `Mencari klausul hukum perpajakan yang relevan (${regListStr}) pada repositori regulasi.`,
      },
      {
        after: 16,
        title: isEnglish ? "Statutory Analysis & Provision Cross-Check" : "Analisis Yuridis & Penafsiran Ketentuan",
        desc: isEnglish
          ? "Cross-checking ministerial regulations, statutory hierarchy, and implementation circulars."
          : "Menganalisis keterkaitan antar-regulasi, hierarki perundang-undangan, dan juklak pelaksanaan teknis.",
      },
      {
        after: 32,
        title: isEnglish ? "Citation Verification (Chain-of-Verification)" : "Verifikasi Sitasi Hukum (Chain-of-Verification)",
        desc: isEnglish
          ? "Verifying article references, validity status (active vs revoked), and cross-checking citations."
          : "Memvalidasi keabsahan pasal, menyaring aturan yang telah dicabut, dan memverifikasi ketepatan sitasi.",
      },
      {
        after: 52,
        title: isEnglish ? "Substantive Review & Consistency Validation" : "Review Substantif & Validasi Argumen",
        desc: isEnglish
          ? "Performing critical review to ensure completeness, logical consistency, and practical relevance."
          : "Melakukan penelaahan kritis untuk memastikan kelengkapan jawaban dan keselarasan dengan praktik audit.",
      },
      {
        after: 72,
        title: isEnglish ? "Synthesizing Structured Response" : "Sintesis & Perumusan Solusi Terstruktur",
        desc: isEnglish
          ? "Synthesizing precise, executive-grade tax consultation response with clear actionable steps."
          : "Menyusun jawaban konsultasi yang komprehensif, runtut, dan mudah dipahami oleh Wajib Pajak.",
      },
    ];

    if (seconds < 10) {
      liveThought = "Memahami konteks pertanyaan dan memetakan peraturan perpajakan yang relevan...";
    } else if (seconds < 25) {
      liveThought = `Mengambil pasal dan regulasi pendukung (${regListStr}) dari database pengetahuan...`;
    } else if (seconds < 45) {
      liveThought = "Menganalisis implikasi hak dan kewajiban perpajakan berdasarkan ketentuan yang berlaku...";
    } else if (seconds < 70) {
      liveThought = "Menjalankan verifikasi sitasi hukum (CoVe) untuk menjamin akurasi rujukan pasal...";
    } else if (seconds < 95) {
      liveThought = "Melakukan review substantif untuk memastikan jawaban tuntas dan tidak membingungkan...";
    } else {
      liveThought = "Menuntaskan format penyajian jawaban dengan bahasa profesional dan terstruktur...";
    }
  }

  return { steps, liveThought };
}

// Inline SVGs for consistent rendering without external packages
function BrainIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 select-none">
      <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 4.44-2.04Z" />
      <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-4.44-2.04Z" />
    </svg>
  );
}

function CheckmarkIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 select-none">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 animate-spin select-none">
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}

function TerminalIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 select-none">
      <polyline points="4 17 10 11 4 5" />
      <line x1="12" y1="19" x2="20" y2="19" />
    </svg>
  );
}

function ChevronDownIcon({ className = "" }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`shrink-0 transition-transform duration-200 ${className}`}>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

export default function ThinkingIndicator({ question = "" }) {
  const { isEnglish } = useLanguage();
  const [elapsed, setElapsed] = useState(0);
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed((prev) => prev + 100);
    }, 100);
    return () => clearInterval(interval);
  }, []);

  const seconds = Math.floor(elapsed / 1000);
  const timeStr =
    seconds < 60
      ? `${seconds}s`
      : `${Math.floor(seconds / 60)}m ${seconds % 60}s`;

  // Parse context from the actual user question
  const context = useMemo(() => extractQueryContext(question), [question]);
  const { steps, liveThought } = useMemo(
    () => buildThinkingPlan(context, seconds, isEnglish),
    [context, seconds, isEnglish]
  );

  // Determine which step is currently active
  let activeStepIndex = 0;
  for (let i = steps.length - 1; i >= 0; i--) {
    if (seconds >= steps[i].after) {
      activeStepIndex = i;
      break;
    }
  }

  const currentStep = steps[activeStepIndex] || steps[0];

  return (
    <div className="flex justify-start z-10 mb-2 mt-1">
      <div className="max-w-[94%] sm:max-w-[85%] md:max-w-[78%] w-full">
        <div className="bg-slate-800/95 border border-slate-700/70 rounded-xl overflow-hidden shadow-xl shadow-slate-950/40 backdrop-blur-sm transition-all duration-300">
          {/* ── HEADER (Clickable to Collapse/Expand) ── */}
          <button
            type="button"
            onClick={() => setIsExpanded((prev) => !prev)}
            className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-slate-750/70 transition-colors focus:outline-none"
            aria-expanded={isExpanded}
          >
            {/* Pulsing thinking indicator */}
            <div className="relative flex h-3 w-3 shrink-0 items-center justify-center">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-60" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 shadow-sm shadow-amber-500/50" />
            </div>

            {/* Icon + Title */}
            <div className="flex items-center gap-2 text-amber-400 shrink-0 font-medium text-[13px]">
              <BrainIcon />
              <span className="font-semibold text-slate-100">
                {isEnglish ? "Thinking Process" : "Proses Berpikir TACIA"}
              </span>
            </div>

            {/* Current Active Thought Summary (shown in header) */}
            <div className="hidden sm:flex flex-1 min-w-0 items-center gap-1.5 text-xs text-slate-300 truncate">
              <span className="text-slate-500 font-mono">/</span>
              <span className="truncate font-medium text-amber-300/90">
                {currentStep.title}
              </span>
            </div>

            {/* Elapsed Time Badge */}
            <span className="ml-auto shrink-0 px-2 py-0.5 rounded-md bg-slate-900/80 border border-slate-700/60 text-slate-300 font-mono text-[11px] font-medium tracking-tight">
              {timeStr}
            </span>

            {/* Chevron toggle arrow */}
            <ChevronDownIcon
              className={isExpanded ? "rotate-180 text-slate-300" : "text-slate-400"}
            />
          </button>

          {/* ── EXPANDED REASONING DRAWER (Claude-style live thinking) ── */}
          {isExpanded && (
            <div className="border-t border-slate-700/60 bg-slate-850/60 px-4 py-3.5 space-y-3.5 animate-fadeIn">
              {/* Cognitive Steps Timeline */}
              <div className="relative pl-1 space-y-3">
                {/* Vertical connecting line */}
                <div className="absolute left-[13px] top-2.5 bottom-2.5 w-[1.5px] bg-slate-750" />

                {steps.map((step, idx) => {
                  const isCompleted = idx < activeStepIndex;
                  const isActive = idx === activeStepIndex;
                  const isPending = idx > activeStepIndex;

                  return (
                    <div key={idx} className="relative flex items-start gap-3 text-xs group">
                      {/* Status indicator node */}
                      <div
                        className={`
                          relative z-10 flex h-5 w-5 shrink-0 items-center justify-center rounded-full transition-all duration-300
                          ${
                            isCompleted
                              ? "bg-emerald-500/20 border border-emerald-500/50 text-emerald-400 shadow-sm shadow-emerald-900/30"
                              : isActive
                              ? "bg-amber-500/20 border-2 border-amber-400 text-amber-300 shadow-sm shadow-amber-500/40"
                              : "bg-slate-800 border border-slate-700 text-slate-500"
                          }
                        `}
                      >
                        {isCompleted && <CheckmarkIcon />}
                        {isActive && <SpinnerIcon />}
                        {isPending && <span className="h-1.5 w-1.5 rounded-full bg-slate-600" />}
                      </div>

                      {/* Step content */}
                      <div className="flex-1 min-w-0 pt-0.5">
                        <div className="flex items-center gap-2">
                          <span
                            className={`font-semibold tracking-tight ${
                              isActive
                                ? "text-amber-300 text-[12.5px]"
                                : isCompleted
                                ? "text-slate-200"
                                : "text-slate-400"
                            }`}
                          >
                            {step.title}
                          </span>
                          {isActive && (
                            <span className="text-[10px] font-mono px-1.5 py-0.2 rounded bg-amber-500/20 text-amber-300 font-medium border border-amber-500/30">
                              {isEnglish ? "ACTIVE" : "AKTIF"}
                            </span>
                          )}
                          {isCompleted && (
                            <span className="text-[10px] font-mono px-1.5 py-0.2 rounded bg-emerald-500/10 text-emerald-400 font-medium">
                              {isEnglish ? "DONE" : "SELESAI"}
                            </span>
                          )}
                        </div>

                        {/* Step detailed rationale */}
                        {(isActive || isCompleted) && (
                          <p
                            className={`mt-0.5 text-[11.5px] leading-relaxed ${
                              isActive ? "text-slate-300" : "text-slate-400"
                            }`}
                          >
                            {step.desc}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* ── REAL-TIME THOUGHT STREAM (Console-style inner thoughts) ── */}
              <div className="mt-3 pt-2.5 border-t border-slate-750/80">
                <div className="bg-slate-900/90 border border-slate-700/60 rounded-lg p-2.5 flex items-start gap-2.5">
                  <div className="mt-0.5 text-amber-400 shrink-0">
                    <TerminalIcon />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-semibold">
                        {isEnglish ? "Live Reasoning Stream" : "Alur Penalaran Nyata (Real-Time)"}
                      </span>
                      <span className="flex h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    </div>
                    <p className="text-[11.5px] font-mono text-amber-200/90 leading-relaxed break-words">
                      {liveThought}
                      <span className="inline-block w-1.5 h-3 ml-1 bg-amber-400 align-middle animate-pulse" />
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── PROGRESS BAR (Honest, continuous dynamic feedback) ── */}
          <div className="w-full h-1 bg-slate-750 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-amber-500 via-amber-300 to-amber-500 transition-all duration-300 ease-out"
              style={{
                width: `${Math.min(98, 15 + Math.log2(seconds + 1) * 14)}%`,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
